export type GL = WebGL2RenderingContext;

export interface Program {
  prog: WebGLProgram;
  u: Record<string, WebGLUniformLocation | null>;
}

const HEADER = '#version 300 es\nprecision highp float;\nprecision highp int;\nprecision highp sampler2DArray;\nprecision highp sampler2DShadow;\n';

export function program(gl: GL, vs: string, fs: string, defines: Record<string, string | number | boolean> = {}): Program {
  const defs = Object.entries(defines).filter(([, v]) => v !== false)
    .map(([k, v]) => `#define ${k} ${v === true ? 1 : v}\n`).join('');
  const sh = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, HEADER + defs + src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(s) ?? '';
      const lines = (HEADER + defs + src).split('\n').map((l, i) => `${i + 1}: ${l}`);
      const m = /0:(\d+)/.exec(log);
      const at = m ? lines.slice(Math.max(0, +m[1] - 4), +m[1] + 2).join('\n') : '';
      throw new Error(`shader: ${log}\n${at}`);
    }
    return s;
  };
  const prog = gl.createProgram()!;
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, vs));
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(`link: ${gl.getProgramInfoLog(prog)}`);
  const u: Program['u'] = {};
  const n = gl.getProgramParameter(prog, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < n; i++) {
    const info = gl.getActiveUniform(prog, i)!;
    const name = info.name.replace(/\[0\]$/, '');
    u[name] = gl.getUniformLocation(prog, info.name);
  }
  return { prog, u };
}

export interface Mesh {
  vao: WebGLVertexArrayObject;
  count: number;
  buffers: WebGLBuffer[];
  indexType: number;
}

/** interleaved float attributes, in order, each of `sizes[i]` floats */
export function mesh(gl: GL, data: Float32Array, sizes: number[], index: Uint32Array | Uint16Array): Mesh {
  const vao = gl.createVertexArray()!;
  gl.bindVertexArray(vao);
  const vb = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  const stride = sizes.reduce((a, b) => a + b, 0) * 4;
  let off = 0;
  sizes.forEach((s, i) => {
    gl.enableVertexAttribArray(i);
    gl.vertexAttribPointer(i, s, gl.FLOAT, false, stride, off);
    off += s * 4;
  });
  const ib = gl.createBuffer()!;
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, index, gl.STATIC_DRAW);
  gl.bindVertexArray(null);
  return { vao, count: index.length, buffers: [vb, ib], indexType: index instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT };
}

export function drawMesh(gl: GL, m: Mesh) {
  gl.bindVertexArray(m.vao);
  gl.drawElements(gl.TRIANGLES, m.count, m.indexType, 0);
}

export function freeMesh(gl: GL, m: Mesh | null | undefined) {
  if (!m) return;
  gl.deleteVertexArray(m.vao);
  m.buffers.forEach(b => gl.deleteBuffer(b));
}

export interface Target {
  fb: WebGLFramebuffer;
  tex: WebGLTexture | null;
  depth: WebGLTexture | null;
  w: number;
  h: number;
}

export function texture(gl: GL, w: number, h: number, internal: number, format: number, type: number, filter: number = gl.LINEAR): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, type, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

export function target(gl: GL, w: number, h: number, opts: { hdr: boolean; depth?: boolean; color?: boolean }): Target {
  const fb = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  let tex: WebGLTexture | null = null, depth: WebGLTexture | null = null;
  if (opts.color !== false) {
    tex = opts.hdr ? texture(gl, w, h, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT) : texture(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  }
  if (opts.depth) {
    depth = texture(gl, w, h, gl.DEPTH_COMPONENT24, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, gl.NEAREST);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, depth, 0);
  }
  if (opts.color === false) { gl.drawBuffers([gl.NONE]); gl.readBuffer(gl.NONE); }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return { fb, tex, depth, w, h };
}

export function freeTarget(gl: GL, t: Target | null | undefined) {
  if (!t) return;
  gl.deleteFramebuffer(t.fb);
  if (t.tex) gl.deleteTexture(t.tex);
  if (t.depth) gl.deleteTexture(t.depth);
}

/** a multisampled colour+depth pair to draw into, resolved into a Target with blitFramebuffer */
export interface MSTarget { fb: WebGLFramebuffer; rbs: WebGLRenderbuffer[]; w: number; h: number; samples: number }

export function msTarget(gl: GL, w: number, h: number, hdr: boolean, samples: number): MSTarget {
  const fb = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  const c = gl.createRenderbuffer()!, d = gl.createRenderbuffer()!;
  gl.bindRenderbuffer(gl.RENDERBUFFER, c);
  gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, hdr ? gl.RGBA16F : gl.RGBA8, w, h);
  gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, c);
  gl.bindRenderbuffer(gl.RENDERBUFFER, d);
  gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, gl.DEPTH_COMPONENT24, w, h);
  gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, d);
  const ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (!ok) throw new Error('msaa target incomplete');
  return { fb, rbs: [c, d], w, h, samples };
}

export function freeMS(gl: GL, t: MSTarget | null | undefined) {
  if (!t) return;
  gl.deleteFramebuffer(t.fb);
  t.rbs.forEach(r => gl.deleteRenderbuffer(r));
}

export const FULLSCREEN_VS = `
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;
